import * as React from "react";
import * as ReactDOM from "react-dom";
import * as ReactDOMClient from "react-dom/client";
import { EditorState } from "prosemirror-state";
import { deepEqual } from "fast-equals";
import RichTextEditor from "./RichTextEditor";
import { fromHtml, initialEditorState, toHtml } from "./Schema";
import { fromRawText, fromStf, ScorableTextFormat, toStf } from "./ScorableTextFormat";
import { ExternalAnnotation, InternalAnnotation } from "./Annotations";
import { ToolbarOptions } from "./Toolbar";

export interface Props {
	contentId?: number;
	initialHtml?: string;
	initialText?: {
		text: string;
		annotations?: ExternalAnnotation[];
	};
	initialStf?: ScorableTextFormat;
	/** Raised whenever the actual rich text content have changed. */
	onDocumentChanged?(): void;
	/** Raised whenever the annotation have changed. */
	onAnnotationsChanged?(): void;
	disablePasteDrop?: boolean;
	characterLimit?: number;
	deleteThreshold?: number;
	dialogActions?: {
		alert(title: string, message: string): Promise<void>;
		confirm(title: string, message: string): Promise<boolean>;
	};
	toolbarOptions?: ToolbarOptions;
	/** This can be toggled later through setReadonly */
	readonly?: boolean;
	id?: string;
}
export interface State {
	editor: EditorState;
	annotations: InternalAnnotation[];
	readonly: boolean;
}

export default class RichTextWrapper extends React.Component<Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = this.initializeState(props);
	}

	initializeState = (props: Props) => {
		if (props.initialHtml) {
			return {
				editor: fromHtml(props.initialHtml),
				annotations: [],
				readonly: !!props.readonly,
			};
		} else if (props.initialText) {
			const conversionResult = fromRawText(
				props.initialText.text,
				props.initialText.annotations ?? []
			);
			return {
				editor: conversionResult.state,
				annotations: conversionResult.annotations,
				readonly: !!props.readonly,
			};
		} else if (props.initialStf) {
			const conversionResult = fromStf(props.initialStf);
			return {
				editor: conversionResult.state,
				annotations: conversionResult.annotations,
				readonly: !!props.readonly,
			};
		} else {
			return {
				editor: initialEditorState,
				annotations: [],
				readonly: !!props.readonly,
			};
		}
	};

	changeEditorState = (newState: EditorState) => {
		this.setState({ editor: newState });
	};

	changeAnnotations = (newAnnotations: InternalAnnotation[]) => {
		this.setState({ annotations: newAnnotations });
	};

	componentDidUpdate(prevProps: Props, prevState: State) {
		// console.log("ExternalApi - componentDidUpdate");
		if (prevProps.contentId !== this.props.contentId) {
			this.setState(this.initializeState(this.props));
		}
		if (
			this.props.onAnnotationsChanged &&
			!deepEqual(prevState.annotations, this.state.annotations)
		) {
			setTimeout(this.props.onAnnotationsChanged, 0);
		}
		if (
			this.props.onDocumentChanged &&
			!deepEqual(prevState.editor.doc, this.state.editor.doc)
		) {
			setTimeout(this.props.onDocumentChanged, 0);
		}
	}

	render() {
		return (
			<RichTextEditor
				state={this.state.editor}
				changeState={this.changeEditorState}
				annotations={this.state.annotations}
				changeAnnotations={this.changeAnnotations}
				readonly={this.state.readonly}
				disablePasteDrop={this.props.disablePasteDrop}
				characterLimit={this.props.characterLimit}
				deleteThreshold={this.props.deleteThreshold}
				dialogActions={this.props.dialogActions}
				toolbarOptions={this.props.toolbarOptions}
				id={this.props.id}
			/>
		);
	}
}

function createRichTextEditorImpl(props: Props & { node: Element }) {
	let ref: RichTextWrapper | null = null;
	const { node, ...rest } = props;

	const root = ReactDOMClient.createRoot(node);
	const content = (
		<RichTextWrapper
			ref={(instance) => {
				ref = instance;
			}}
			{...rest}
		/>
	);

	root.render(content);

	return {
		destroy() {
			if (ref) {
				ReactDOM.unmountComponentAtNode(node);
			}
		},
		getHtml() {
			if (ref) {
				return toHtml(ref.state.editor);
			}
		},
		getStf() {
			if (ref) {
				return toStf(ref.state.editor, ref.state.annotations);
			}
		},
		setReadonly(readonly: boolean) {
			if (ref) {
				ref.setState({ readonly: !!readonly });
			}
		},
	};
}

export function stfToHtmlImpl(stf: ScorableTextFormat) {
	const conversionResult = fromStf(stf);
	return toHtml(conversionResult.state);
}

declare global {
	interface Window {
		createRichTextEditor: typeof createRichTextEditorImpl;
		stfToHtml: typeof stfToHtmlImpl;
	}
}

window.createRichTextEditor = createRichTextEditorImpl;
window.stfToHtml = stfToHtmlImpl;
